import React, { useState, useEffect } from 'react';
import { AggregatedOrder, CalculatedOrder, CalculatedOrdersSummary } from '@/components/orders/types';
import { getCalculatedBuyOrders, getCalculatedSellOrders } from './api';
import { formatDate, formatCurrency, formatNumber, formatSummaryAmount } from '@/utils/formatters';
import OrderSectionHeader from '@/components/orders/OrderSectionHeader';
import TableWrapper from '@/components/orders/TableWrapper';
import styles from '@/components/orders/OrderManagement.module.css';
import { useIsMounted } from '@/hooks/useIsMounted';
import shared from '@/components/utils/shared.module.css';
import { Note, Placeholder } from '@/components/ui';
import OrderStatusPill, { calculatedOrderStage } from '@/components/orders/orderStatus';

const BlockCalculatedOrders: React.FC = () => {
  const [aggregatedBuyOrders, setAggregatedBuyOrders] = useState<AggregatedOrder[]>([]);
  const [aggregatedSellOrders, setAggregatedSellOrders] = useState<AggregatedOrder[]>([]);
  const [nriBuyOrders, setNriBuyOrders] = useState<CalculatedOrder[]>([]);
  const [nriSellOrders, setNriSellOrders] = useState<CalculatedOrder[]>([]);
  const [buySummary, setBuySummary] = useState<CalculatedOrdersSummary | null>(null);
  const [sellSummary, setSellSummary] = useState<CalculatedOrdersSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useIsMounted();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const params = { page_size: 100, aggregate_non_nri: true, block_trade: true };
        const [buyRes, sellRes] = await Promise.all([getCalculatedBuyOrders(params), getCalculatedSellOrders(params)]);
        if (!isMounted.current) return;
        setAggregatedBuyOrders(buyRes.aggregated_non_nri || []);
        setNriBuyOrders(buyRes.nri_orders || []);
        setBuySummary({ total_count: buyRes.count, total_quantity: buyRes.total_quantity, total_amount: buyRes.total_amount, currency: buyRes.currency });
        setAggregatedSellOrders(sellRes.aggregated_non_nri || []);
        setNriSellOrders(sellRes.nri_orders || []);
        setSellSummary({ total_count: sellRes.count, total_quantity: sellRes.total_quantity, total_amount: sellRes.total_amount, currency: sellRes.currency });
      } catch { if (isMounted.current) setError('Failed to load block calculated orders'); }
      finally { if (isMounted.current) setLoading(false); }
    })();
  }, []);

  const renderSection = (type: 'buy' | 'sell') => {
    const aggOrders = type === 'buy' ? aggregatedBuyOrders : aggregatedSellOrders;
    const nriOrders = type === 'buy' ? nriBuyOrders : nriSellOrders;
    const summary = type === 'buy' ? buySummary : sellSummary;
    const label = type === 'buy' ? 'Buy' : 'Sell';

    return (
      <div className={styles['order-section']}>
        <OrderSectionHeader side={type} title={`Block Calculated ${label} Orders`} />

        {summary && (
          <div className={styles['summary-row']}>
            <div className={styles['summary-chip']}>
              <span className={styles['summary-chip-label']}>Total Orders</span>
              <span className={styles['summary-chip-value']}>{formatNumber(summary.total_count, 0)}</span>
            </div>
            <div className={styles['summary-chip']}>
              <span className={styles['summary-chip-label']}>Total Quantity</span>
              <span className={styles['summary-chip-value']}>{formatNumber(summary.total_quantity, 0)}</span>
            </div>
            <div className={styles['summary-chip']}>
              <span className={styles['summary-chip-label']}>Total Amount</span>
              <span className={styles['summary-chip-value']}>{formatSummaryAmount(summary.total_amount, summary.currency)}</span>
            </div>
          </div>
        )}

        {aggOrders.length === 0 && nriOrders.length === 0 ? (
          <div className={styles.empty}>
            <Placeholder message={<>No block calculated {label.toLowerCase()} orders found.</>} />
          </div>
        ) : (
          <>
            {aggOrders.length > 0 && (
              <div className={styles['sub-section']}>
                <div className={styles['sub-section-header']}>
                  <div className={styles['sub-section-line']} />
                  <span className={styles['badge-non-nri']}>Non-NRI — Aggregated</span>
                  <div className={styles['sub-section-line']} />
                </div>
                <TableWrapper>
                    <table className={`${shared.table} ${styles.table}`}>
                      <thead>
                        <tr>
                          <th>Scheme</th>
                          <th>Company</th>
                          <th className={styles.num}>Orders</th>
                          <th className={styles.num}>Total Qty</th>
                          <th className={styles.num}>Total Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aggOrders.map(order => (
                          <tr key={`${order.scheme.id}-${order.company.id}`}>
                            <td>{order.scheme.code}</td>
                            <td>{order.company.symbol}</td>
                            <td className={styles.num}>{order.order_count}</td>
                            <td className={`${styles['text-mono']} ${styles.num}`}>{formatNumber(order.total_quantity, 0)}</td>
                            <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(order.total_amount, order.scheme.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </TableWrapper>
              </div>
            )}

            {nriOrders.length > 0 && (
              <div className={styles['sub-section']}>
                <div className={styles['sub-section-header']}>
                  <div className={styles['sub-section-line']} />
                  <span className={styles['badge-nri']}>NRI — Individual</span>
                  <div className={styles['sub-section-line']} />
                </div>
                <TableWrapper>
                    <table className={`${shared.table} ${styles.table}`}>
                      <thead>
                        <tr>
                          <th>Order Date</th>
                          <th>Scheme</th>
                          <th>Company</th>
                          <th>Client Code</th>
                          <th>Client Name</th>
                          <th className={styles.num}>Quantity</th>
                          <th className={styles.num}>Amount</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nriOrders.map(order => (
                          <tr key={order.id}>
                            <td>{formatDate(order.order_date)}</td>
                            <td>{order.scheme.code}</td>
                            <td>{order.company.symbol}</td>
                            <td>{order.client.code}</td>
                            <td>{order.client.name}</td>
                            <td className={`${styles['text-mono']} ${styles.num}`}>{formatNumber(order.quantity, 0)}</td>
                            <td className={`${styles['text-mono']} ${styles.num}`}>{formatCurrency(order.amount, order.scheme.currency)}</td>
                            <td>
                              <OrderStatusPill stage={calculatedOrderStage(order.order_status)} label={order.order_status_display} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </TableWrapper>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (loading) return <Placeholder kind="loading" />;

  return (
    <div>
      {error && <div className={styles.messages}><Note tone="error">{error}</Note></div>}
      {renderSection('buy')}
      {renderSection('sell')}
    </div>
  );
};

export default BlockCalculatedOrders;